import { useEffect } from 'react';

/**
 * Silent PWA auto-update.
 *
 * The service worker is generated with `registerType: 'autoUpdate'`, so a new
 * worker skips the waiting phase and immediately claims clients — which fires
 * `controllerchange`. We reload once at that point so the freshly deployed
 * assets are served. There is no user-facing prompt: an update lands on the
 * next visit/navigation (or within the hourly poll for a long-lived tab).
 */
export const usePWAUpdate = (): void => {
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    // Only auto-reload on a genuine update — i.e. the page was already
    // controlled by an older worker at load time. This avoids a redundant
    // reload on the very first service-worker install.
    const controlledAtLoad = !!navigator.serviceWorker.controller;
    let refreshing = false;

    const handleControllerChange = () => {
      if (refreshing || !controlledAtLoad) {
        return;
      }
      refreshing = true;
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Poll so a tab left open also eventually picks up a new worker.
    const updateInterval = setInterval(() => {
      navigator.serviceWorker.ready
        .then((registration) => registration.update())
        .catch(() => {});
    }, 60 * 60 * 1000);

    // Check once on mount.
    navigator.serviceWorker.ready
      .then((registration) => registration.update())
      .catch(() => {});

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      clearInterval(updateInterval);
    };
  }, []);
};
